import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, Info, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type {
  TelegramChatSummary,
  TelegramConnectLink,
  TelegramDirectLink,
  TelegramOneClickAvailability,
} from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import {
  TELEGRAM_CHAT_STATE_PENDING,
  TelegramChatRow,
  isTelegramGroupChat,
} from '@/Domain/components/Integration/Telegram/TelegramChatRow';
import { CopyField } from '@/Domain/components/Integration/components/CopyField';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationSetupOption } from '@/Domain/components/Integration/components/IntegrationSetupOption';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

// Telegram desktop can consume the deep link without triggering a focus refetch.
const CONNECT_RECONCILE_WINDOW_MS = 3 * 60 * 1000;
const CONNECT_RECONCILE_INTERVAL_MS = 5000;

const isPending = (chat: TelegramChatSummary) => chat.state === TELEGRAM_CHAT_STATE_PENDING;

/**
 * Whether the shared-bot option is certainly on screen. The sheet reads it to decide
 * whether to name the own-bot form as the advanced alternative. Deliberately the
 * certain subset of showChats/showDirectLink below: when a management probe comes back
 * unknown the section may still render, and the sheet then simply reads as it did
 * before the option labels existed rather than announcing an alternative to nothing.
 */
export const hasOneClickSurface = (availability?: TelegramOneClickAvailability) =>
  availability?.available === true ||
  availability?.direct_link_active === true ||
  availability?.has_connected_chats === true;

export const TelegramOneClickSection = () => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const [busyChatIds, setBusyChatIds] = useState<readonly string[]>([]);
  // Spend reconciliation on a new chat id, not a list-length change.
  const [reconcile, setReconcile] = useState<{ until: number; knownChatIds: string[] } | null>(
    null,
  );
  const [confirmRemove, setConfirmRemove] = useState<TelegramChatSummary | null>(null);

  const markBusy = (chatId: string) => setBusyChatIds((busy) => [...busy, chatId]);
  const clearBusy = (chatId: string) =>
    setBusyChatIds((busy) => busy.filter((id) => id !== chatId));
  // Set only when the browser refused the tab, so the user still has something to click.
  const [blockedConnectUrl, setBlockedConnectUrl] = useState<string | null>(null);

  const { data: availability, isFetching: availabilityLoading } =
    useQuery<TelegramOneClickAvailability>({
      queryKey: [QueryKey.integration.telegramOneClickAvailability],
    });
  const isAvailable = availability?.available === true;
  const hasDirectLink = availability?.direct_link_active === true;
  const directLinkStateUnknown = availability?.direct_link_active === null;
  const shouldReadDirectLink = isAvailable || hasDirectLink || directLinkStateUnknown;
  const hasConnectedChats = availability?.has_connected_chats === true;
  const connectedChatsStateUnknown = availability?.has_connected_chats === null;

  const {
    data: chats = [],
    isLoading,
    error: chatsError,
    refetch: refetchChats,
  } = useQuery<TelegramChatSummary[]>({
    queryKey: [QueryKey.integration.telegramChats],
    refetchInterval: (query) =>
      reconcile &&
      Date.now() < reconcile.until &&
      !(query.state.data ?? [])
        .filter(isTelegramGroupChat)
        .some((chat) => !reconcile.knownChatIds.includes(chat.chat_id))
        ? CONNECT_RECONCILE_INTERVAL_MS
        : false,
  });

  const invalidateChats = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKey.integration.telegramChats] }),
      queryClient.invalidateQueries({
        queryKey: [QueryKey.integration.telegramOneClickAvailability],
      }),
    ]);

  const connectLinkMutation = useMutation<TelegramConnectLink, unknown, void>({
    mutationKey: [QueryKey.integration.telegramConnectLink],
  });

  const confirmMutation = useMutation<TelegramChatSummary, unknown, { chatId: string }>({
    mutationKey: [QueryKey.integration.telegramChatConfirm],
  });

  const patchMutation = useMutation<
    TelegramChatSummary,
    unknown,
    { chatId: string; enabled: boolean }
  >({ mutationKey: [QueryKey.integration.telegramChatPatch] });

  const removeMutation = useMutation<void, unknown, { chatId: string }>({
    mutationKey: [QueryKey.integration.telegramChatDelete],
  });

  const {
    data: directLink,
    isFetching: directLinkLoading,
    error: directLinkError,
    refetch: refetchDirectLink,
  } = useQuery<TelegramDirectLink>({
    queryKey: [QueryKey.integration.telegramDirectLink],
    enabled: shouldReadDirectLink,
  });

  const directLinkMutation = useMutation<TelegramDirectLink, unknown, { active: boolean }>({
    mutationKey: [QueryKey.integration.telegramDirectLinkPatch],
  });

  const handleAddToGroup = async () => {
    // Open synchronously because Safari drops popups after the first await.
    const telegramTab = window.open('', '_blank');
    setBlockedConnectUrl(null);
    trackIntegrationConnectStarted(capture, 'telegram');
    try {
      const link = await connectLinkMutation.mutateAsync();
      if (telegramTab) {
        telegramTab.opener = null;
        telegramTab.location.replace(link.url);
      } else {
        setBlockedConnectUrl(link.url);
        trackIntegrationConnectFailed(capture, 'telegram', 'popup_blocked');
      }
      capture(PostHogEventEnum.TelegramIntegrationConnectLinkOpened, {
        failed: false,
        popup_blocked: !telegramTab,
      });
      setReconcile({
        until: Date.now() + CONNECT_RECONCILE_WINDOW_MS,
        knownChatIds: chats.filter(isTelegramGroupChat).map((chat) => chat.chat_id),
      });
    } catch {
      telegramTab?.close();
      capture(PostHogEventEnum.TelegramIntegrationConnectLinkOpened, { failed: true });
      trackIntegrationConnectFailed(capture, 'telegram', 'connect_failed');
      toast.error(t('integration:telegram.oneclick.connect_link_failed'));
    }
  };

  const handleConfirm = async (chat: TelegramChatSummary) => {
    markBusy(chat.chat_id);
    try {
      await confirmMutation.mutateAsync({ chatId: chat.chat_id });
      capture(PostHogEventEnum.TelegramIntegrationChatConfirmed, {
        chat_type: chat.chat_type,
      });
      // Confirmation is the terminal connection event because pending chats stay silent.
      trackIntegrationConnectSucceeded(capture, 'telegram');
      toast.success(t('integration:telegram.oneclick.confirmed'));
    } catch {
      trackIntegrationConnectFailed(capture, 'telegram', 'confirm_failed');
      toast.error(t('integration:telegram.oneclick.confirm_failed'));
    } finally {
      await invalidateChats();
      clearBusy(chat.chat_id);
    }
  };

  const handleToggleEnabled = async (chatId: string, enabled: boolean) => {
    markBusy(chatId);
    try {
      await patchMutation.mutateAsync({ chatId, enabled });
      capture(PostHogEventEnum.TelegramIntegrationChatToggled, { enabled });
    } catch {
      toast.error(t('integration:telegram.oneclick.save_failed'));
    } finally {
      await invalidateChats();
      clearBusy(chatId);
    }
  };

  const handleRemove = async (chat: TelegramChatSummary) => {
    setConfirmRemove(null);
    markBusy(chat.chat_id);
    try {
      await removeMutation.mutateAsync({ chatId: chat.chat_id });
      capture(PostHogEventEnum.TelegramIntegrationChatRemoved, {
        chat_type: chat.chat_type,
      });
      toast.success(t('integration:telegram.oneclick.removed'));
    } catch {
      toast.error(t('integration:telegram.oneclick.remove_failed'));
    } finally {
      // A failed Telegram leave still removes routing state before returning 503.
      await invalidateChats();
      clearBusy(chat.chat_id);
    }
  };

  const handleToggleDirectLink = async (active: boolean) => {
    try {
      await directLinkMutation.mutateAsync({ active });
      capture(PostHogEventEnum.TelegramIntegrationDirectLinkToggled, { active });
    } catch {
      toast.error(t('integration:telegram.oneclick.save_failed'));
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKey.integration.telegramDirectLink],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.integration.telegramOneClickAvailability],
        }),
      ]);
    }
  };

  const groupChats = chats.filter(isTelegramGroupChat);
  // The connect link is single-use; clicking a spent one can only fail invalid_nonce.
  const boundSinceConnect =
    reconcile !== null && groupChats.some((chat) => !reconcile.knownChatIds.includes(chat.chat_id));
  const unspentConnectUrl = boundSinceConnect ? null : blockedConnectUrl;
  const pendingChats = groupChats.filter(isPending);
  const activeChats = groupChats.filter((chat) => !isPending(chat));
  const showChats =
    isAvailable ||
    groupChats.length > 0 ||
    hasConnectedChats ||
    (connectedChatsStateUnknown && Boolean(chatsError));
  const showDirectLink =
    isAvailable ||
    hasDirectLink ||
    (directLinkStateUnknown &&
      (directLinkLoading || Boolean(directLinkError) || directLink?.active === true));

  if (isLoading || availabilityLoading) {
    return (
      <div className="flex justify-center py-8" data-testid="telegram-oneclick-loading">
        <Spinner />
      </div>
    );
  }

  if (!showChats && !showDirectLink) return null;

  return (
    <IntegrationSetupOption
      title={t('integration:telegram.setup.shared.title')}
      badge={t('integration:telegram.setup.shared.badge')}
      badgeVariant="primary"
      description={t('integration:telegram.setup.shared.description')}
      data-testid="telegram-oneclick-section"
    >
      <div className="space-y-6">
        {!isAvailable && (
          <Alert variant="warning" data-testid="telegram-oneclick-unavailable">
            <AlertTriangle className="size-4" />
            <AlertDescription>{t('integration:telegram.oneclick.unavailable')}</AlertDescription>
          </Alert>
        )}

        {showChats && chatsError ? (
          <Alert variant="destructive" data-testid="telegram-oneclick-load-error">
            <AlertCircle className="size-4 !text-destructive" />
            <AlertDescription className="space-y-3">
              <p className="m-0">{t('integration:telegram.oneclick.load_failed')}</p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void refetchChats()}
                data-testid="telegram-oneclick-load-retry"
              >
                {t('integration:telegram.oneclick.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : showChats ? (
          <>
            {pendingChats.length > 0 && (
              <IntegrationSection
                title={t('integration:telegram.oneclick.awaiting_confirmation')}
                titleClassName="text-sm"
                data-testid="telegram-pending-section"
              >
                <div className="flex flex-col gap-3">
                  {pendingChats.map((chat) => (
                    <TelegramChatRow
                      key={chat.chat_id}
                      chat={chat}
                      onToggleEnabled={handleToggleEnabled}
                      onRemove={setConfirmRemove}
                      onConfirm={handleConfirm}
                      disabled={busyChatIds.includes(chat.chat_id)}
                      readinessBlocked={!isAvailable}
                    />
                  ))}
                </div>
              </IntegrationSection>
            )}

            <IntegrationSection
              title={t('integration:telegram.oneclick.connected_chats')}
              titleClassName="text-sm"
              description={t('integration:telegram.oneclick.connected_chats_description')}
              data-testid="telegram-chats-section"
            >
              <div className="flex flex-col gap-3">
                {activeChats.map((chat) => (
                  <TelegramChatRow
                    key={chat.chat_id}
                    chat={chat}
                    onToggleEnabled={handleToggleEnabled}
                    onRemove={setConfirmRemove}
                    onConfirm={handleConfirm}
                    disabled={busyChatIds.includes(chat.chat_id)}
                    readinessBlocked={!isAvailable}
                  />
                ))}
                {activeChats.length === 0 && pendingChats.length === 0 && (
                  <p
                    className="m-0 text-muted-foreground text-sm"
                    data-testid="telegram-chats-empty"
                  >
                    {t('integration:telegram.oneclick.no_chats')}
                  </p>
                )}
                {isAvailable && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleAddToGroup()}
                    disabled={connectLinkMutation.isPending}
                    className="self-start"
                    data-testid="telegram-add-to-group"
                  >
                    <Plus className="size-4" />
                    {connectLinkMutation.isPending
                      ? t('integration:telegram.oneclick.opening')
                      : t(
                          groupChats.length > 0
                            ? 'integration:telegram.oneclick.add_another'
                            : 'integration:telegram.oneclick.add_to_group',
                        )}
                  </Button>
                )}
                {unspentConnectUrl && (
                  <Alert data-testid="telegram-connect-popup-blocked">
                    <Info className="size-4" />
                    <AlertDescription>
                      {t('integration:telegram.oneclick.popup_blocked')}{' '}
                      <a
                        href={unspentConnectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground link-underline underline-offset-2"
                        data-testid="telegram-connect-popup-blocked-link"
                      >
                        {t('integration:telegram.oneclick.open_telegram')}
                      </a>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </IntegrationSection>
          </>
        ) : null}

        {showDirectLink && (
          <IntegrationSection
            title={t('integration:telegram.oneclick.direct_link_title')}
            titleClassName="text-sm"
            data-testid="telegram-direct-link-section"
          >
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <p className="m-0 text-muted-foreground text-sm">
                  {t('integration:telegram.oneclick.direct_link_description')}
                </p>
                {/* A switch we haven't read yet would claim a live public link is off. */}
                {directLinkLoading && (
                  <Spinner className="size-4" data-testid="telegram-direct-link-loading" />
                )}
                {!directLinkLoading && !directLinkError && (
                  <Switch
                    id="telegram-direct-link-active"
                    checked={Boolean(directLink?.active)}
                    onCheckedChange={(checked) => void handleToggleDirectLink(checked)}
                    // Switching a live link off is not readiness-gated; switching one on 503s.
                    disabled={directLinkMutation.isPending || (!isAvailable && !directLink?.active)}
                    aria-label={t('integration:telegram.oneclick.direct_link_toggle_label')}
                    data-testid="telegram-direct-link-toggle"
                  />
                )}
              </div>
              {Boolean(directLinkError) && (
                <Alert variant="destructive" data-testid="telegram-direct-link-error">
                  <AlertCircle className="size-4 !text-destructive" />
                  <AlertDescription className="space-y-3">
                    <p className="m-0">{t('integration:telegram.oneclick.direct_link_failed')}</p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void refetchDirectLink()}
                      data-testid="telegram-direct-link-retry"
                    >
                      {t('integration:telegram.oneclick.retry')}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
              {directLink?.active && (
                <CopyField
                  label={t('integration:telegram.oneclick.direct_link_field_label')}
                  value={directLink.url}
                  testId="telegram-direct-link-url"
                  copyTestId="telegram-direct-link-copy"
                  onCopy={() => capture(PostHogEventEnum.TelegramIntegrationDirectLinkCopied)}
                />
              )}
            </div>
          </IntegrationSection>
        )}

        {/* `absolute inset-0` centers it within the Sheet, not the viewport. */}
        <AlertDialogPrimitive.Root
          open={confirmRemove !== null}
          onOpenChange={(dialogOpen) => !dialogOpen && setConfirmRemove(null)}
        >
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <AlertDialogPrimitive.Content
            className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
            data-testid="telegram-remove-dialog"
          >
            <div className="bg-card rounded-lg p-6 max-w-md w-full shadow-lg space-y-4">
              <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
                {t('integration:telegram.oneclick.remove_confirm_title', {
                  chat: confirmRemove?.title || t('integration:telegram.oneclick.untitled_chat'),
                })}
              </AlertDialogPrimitive.Title>
              <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
                {t('integration:telegram.oneclick.remove_confirm_description')}
              </AlertDialogPrimitive.Description>
              <div className="flex items-center justify-end gap-2 pt-2">
                <AlertDialogPrimitive.Cancel asChild>
                  <Button variant="secondary" size="md" data-testid="telegram-remove-cancel">
                    {t('integration:telegram.oneclick.remove_confirm_cancel')}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => confirmRemove && void handleRemove(confirmRemove)}
                  disabled={removeMutation.isPending}
                  data-testid="telegram-remove-confirm"
                >
                  {t('integration:telegram.oneclick.remove_confirm_confirm')}
                </Button>
              </div>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </div>
    </IntegrationSetupOption>
  );
};

export default TelegramOneClickSection;
